import {
  differenceInMinutes,
  formatDuration,
  parseISO,
  roundToNearestMinutes,
} from 'date-fns';
import { CircleCheckIcon } from 'lucide-react';

import {
  JOB_STATUS_CATEGORIES,
  JOB_STATUS_CATEGORY_COLORS,
  JOB_STATUS_COLORS,
  JOB_STATUS_ICONS,
  JOB_STATUS_LABELS,
} from '@/constant';
import { JOB_STATUS, JobData } from '@/types/jobs';

export const calculateTimeDifference = (job: JobData) => {
  if (!job.plannedDuration) return null;

  const absActualMinutes = Math.abs(job.actualDuration / 60);

  const actualDays = Math.floor(absActualMinutes / 1440);
  const actualHours = Math.floor((absActualMinutes % 1440) / 60);
  const actualMinutes = Math.round(absActualMinutes % 60);

  const actualString = formatDuration(
    { days: actualDays, hours: actualHours, minutes: actualMinutes },
    {
      format: ['days', 'hours', 'minutes'], // Specify the units you want to display
    }
  )
    .replace(/\b(\d+)\s+days?\b/g, '$1d')
    .replace(/\b(\d+)\s+hours?\b/g, '$1h')
    .replace(/\b(\d+)\s+minutes?\b/g, '$1m');

  const absPlannedMinutes = Math.abs(job.plannedDuration);
  const plannedDays = Math.floor(absPlannedMinutes / 1440);
  const plannedHours = Math.floor((absPlannedMinutes % 1440) / 60);
  const plannedMinutes = Math.round(absPlannedMinutes % 60);

  const plannedString = formatDuration(
    { days: plannedDays, hours: plannedHours, minutes: plannedMinutes },
    {
      format: ['days', 'hours', 'minutes'], // Specify the units you want to display
    }
  )
    .replace(/\b(\d+)\s+days?\b/g, '$1d')
    .replace(/\b(\d+)\s+hours?\b/g, '$1h')
    .replace(/\b(\d+)\s+minutes?\b/g, '$1m');

  const difference =
    absActualMinutes && job.actualEndAt
      ? absActualMinutes - absPlannedMinutes
      : 0;

  const absMinutes = Math.abs(difference);
  const differenceDays = Math.floor(absMinutes / 1440);
  const differenceHours = Math.floor((absMinutes % 1440) / 60);
  const differenceMinutes = Math.round(absMinutes % 60);

  const color = difference > 0 ? 'red.400' : 'green.400';

  const differenceInDuration = {
    days: differenceDays,
    hours: differenceHours,
    minutes: differenceMinutes,
  };

  // Format the duration with custom unit labels
  const differenceString = formatDuration(differenceInDuration, {
    format: ['days', 'hours', 'minutes'], // Specify the units you want to display
  })
    .replace(/\b(\d+)\s+days?\b/g, '$1d')
    .replace(/\b(\d+)\s+hours?\b/g, '$1h')
    .replace(/\b(\d+)\s+minutes?\b/g, '$1m');

  return {
    actual: absActualMinutes,
    actualString,
    planned: absPlannedMinutes,
    plannedString,
    difference,
    differenceString,
    color,
  };
};

export const getJobTimelineDifference = (
  actual: string | null,
  planned: string | null
) => {
  if (!actual || !planned) return null;

  const difference = differenceInMinutes(
    roundToNearestMinutes(parseISO(actual)),
    parseISO(planned)
  );

  const absMinutes = Math.abs(difference);
  const differenceDays = Math.floor(absMinutes / 1440);
  const differenceHours = Math.floor((absMinutes % 1440) / 60);
  const differenceMinutes = Math.round(absMinutes % 60);

  const color = difference > 0 ? 'red.400' : 'green.400';

  const differenceInDuration = {
    days: differenceDays,
    hours: differenceHours,
    minutes: differenceMinutes,
  };

  // Format the duration with custom unit labels
  const differenceString = formatDuration(differenceInDuration, {
    format: ['days', 'hours', 'minutes'], // Specify the units you want to display
  })
    .replace(/\b(\d+)\s+days?\b/g, '$1d')
    .replace(/\b(\d+)\s+hours?\b/g, '$1h')
    .replace(/\b(\d+)\s+minutes?\b/g, '$1m');

  return {
    difference,
    differenceHours,
    differenceMinutes,
    differenceString,
    color,
  };
};

/**
 * Utility function to get job status label
 * @param status - The job status enum value
 * @returns The display label for the status
 */
export const getJobStatusLabel = (status: JOB_STATUS): string => {
  return JOB_STATUS_LABELS[status] || status;
};

/**
 * Utility function to get job status color
 * @param status - The job status enum value
 * @returns The color class for the status
 */
export const getJobStatusColor = (status: JOB_STATUS): string => {
  return JOB_STATUS_COLORS[status] || '!text-gray-400';
};

/**
 * Get the category that a job status belongs to
 * @param status - The job status enum value
 * @returns The category name
 */
const getJobStatusCategory = (
  status: JOB_STATUS
): keyof typeof JOB_STATUS_CATEGORIES => {
  // Check each category array
  if ((JOB_STATUS_CATEGORIES.PENDING as readonly JOB_STATUS[]).includes(status))
    return 'PENDING';
  if (
    (JOB_STATUS_CATEGORIES.IN_PROGRESS as readonly JOB_STATUS[]).includes(
      status
    )
  )
    return 'IN_PROGRESS';
  if (
    (JOB_STATUS_CATEGORIES.COMPLETED as readonly JOB_STATUS[]).includes(status)
  )
    return 'COMPLETED';
  if (
    (JOB_STATUS_CATEGORIES.CANCELLED as readonly JOB_STATUS[]).includes(status)
  )
    return 'CANCELLED';
  if ((JOB_STATUS_CATEGORIES.ISSUES as readonly JOB_STATUS[]).includes(status))
    return 'ISSUES';
  return 'PENDING'; // Default fallback
};

/**
 * Get job status category colors with opacity
 * @param status - The job status enum value
 * @returns The category color object with bg, text, and bgBadge classes
 */
export const getJobStatusCategoryColors = (status: JOB_STATUS) => {
  const category = getJobStatusCategory(status);
  return JOB_STATUS_CATEGORY_COLORS[category];
};

/**
 * Get job status icon component
 * @param status - The job status enum value
 * @returns The icon component
 */
export const getJobStatusIcon = (status: JOB_STATUS) => {
  return JOB_STATUS_ICONS[status] || CircleCheckIcon;
};

/**
 * Determines whether to show planned comparison values in timeline
 * @param status - The job status enum value
 * @returns True when timeline should render planned comparison values
 */
export const isJobTimelineExtended = (status: JOB_STATUS): boolean => {
  return ![JOB_STATUS.SCHEDULED, JOB_STATUS.SENT, JOB_STATUS.ACCEPTED].includes(
    status
  );
};
